'use client';

// Reusable manual-trade FORM (issue #360). Buy/sell a ticker into ANY of the
// user's portfolios at an editable price + quantity; the TICKER itself is an
// editable field (prefilled, overridable). Used inside the stock/ETF Trade
// side panel's Trade tab (terminal inline + the global stock/ETF overlay).
//
// Replay-aware via asOfIso/asOfLabel — but ONLY while the ticker still matches
// initialTicker. Changing the ticker switches to a live quote + a NOW() trade,
// since the replay/historical context belongs to the page's own symbol.
//
// All the heavy lifting (cash ledger, position recalc, oversell rejection) is
// server-side: POST /portfolios/:id/transactions.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ArrowDownCircle, ArrowUpCircle, ExternalLink } from 'lucide-react';
import { cn, formatCurrency, formatPct } from '@/lib/utils';
import {
  getPortfolios,
  getPortfolio,
  getStockDetail,
  addTransaction,
  type Portfolio,
  type Position,
} from '@/lib/api-client';

const LAST_PORTFOLIO_KEY = 'tradeModal:lastPortfolioId';

/** UTC ISO → a `<input type="datetime-local">` value (browser-local YYYY-MM-DDTHH:mm). */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface TradeFormProps {
  /** Ticker to prefill; the user can change it in the form. */
  initialTicker: string;
  /** Prefilled price for initialTicker (live / paused-replay / historical). */
  prefillPrice?: number;
  /** Replay/historical as-of (sent as transactedAt) — applied only while the
   *  ticker equals initialTicker. */
  asOfIso?: string | null;
  asOfLabel?: string | null;
  onTraded?: () => void;
  className?: string;
}

export function TradeForm({ initialTicker, prefillPrice, asOfIso, asOfLabel, onTraded, className }: TradeFormProps) {
  const [ticker, setTicker] = useState<string>(initialTicker.toUpperCase());
  const [portfolios, setPortfolios] = useState<Portfolio[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<Portfolio | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [priceStr, setPriceStr] = useState<string>(prefillPrice && prefillPrice > 0 ? prefillPrice.toFixed(2) : '');
  const [qtyStr, setQtyStr] = useState<string>('');
  const [commissionStr, setCommissionStr] = useState<string>('');
  // Transaction date/time (datetime-local, browser TZ). Prefilled from the
  // replay cursor in replay mode (the price's time), else now; editable.
  const [dateTimeStr, setDateTimeStr] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const tickerUC = ticker.trim().toUpperCase();
  const isInitialTicker = tickerUC === initialTicker.trim().toUpperCase();
  // asOf (replay/historical) only applies to the page's own ticker.
  const effAsOfIso = isInitialTicker ? (asOfIso ?? null) : null;
  const effAsOfLabel = isInitialTicker ? (asOfLabel ?? null) : null;

  // Load the user's portfolios; default-select the last-used (or first). Bumping
  // reloadKey (the Retry button) refetches.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    let retried = false;
    setLoadErr(null);
    setPortfolios(null);
    const run = async () => {
      try {
        const list = await getPortfolios();
        if (!alive) return;
        setPortfolios(list);
        const last = (() => { try { return window.localStorage.getItem(LAST_PORTFOLIO_KEY); } catch { return null; } })();
        const pick = (last && list.some((p) => p.id === last)) ? last : (list[0]?.id ?? '');
        setSelectedId(pick);
      } catch (e) {
        if (!alive) return;
        // One automatic retry — the API may have been mid-restart (a Railway
        // deploy returns "Application failed to respond" for a few seconds).
        if (!retried) { retried = true; setTimeout(() => { if (alive) void run(); }, 1500); return; }
        setLoadErr(e instanceof Error ? e.message : 'Failed to load portfolios');
      }
    };
    void run();
    return () => { alive = false; };
  }, [reloadKey]);

  // Selected portfolio's positions + cash (getPortfolios omits positions).
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let alive = true;
    setDetailLoading(true);
    (async () => {
      try {
        const p = await getPortfolio(selectedId);
        if (alive) setDetail(p);
      } catch {
        if (alive) setDetail(null);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  // Price prefill: initial ticker → the passed prefill; a changed ticker → its
  // live quote (debounced). Manual price edits survive until the ticker changes.
  useEffect(() => {
    if (isInitialTicker) {
      setPriceStr(prefillPrice && prefillPrice > 0 ? prefillPrice.toFixed(2) : '');
      return;
    }
    if (tickerUC.length < 1) return;
    let alive = true;
    const id = setTimeout(async () => {
      try {
        const d = await getStockDetail(tickerUC);
        if (alive && d && d.price > 0) setPriceStr(d.price.toFixed(2));
      } catch { /* leave price as-is */ }
    }, 400);
    return () => { alive = false; clearTimeout(id); };
  }, [tickerUC, isInitialTicker, prefillPrice]);

  // Prefill the trade time: the replay cursor's instant when replaying the
  // page's own ticker, otherwise now. Re-runs when the cursor moves / clears.
  useEffect(() => {
    setDateTimeStr(isoToLocalInput(effAsOfIso ?? new Date().toISOString()));
  }, [effAsOfIso]);

  const holding: Position | null = useMemo(() => {
    return detail?.positions?.find((p) => p.ticker.toUpperCase() === tickerUC && p.shares > 0) ?? null;
  }, [detail, tickerUC]);

  // Position numerics can arrive as strings (pg NUMERIC) — coerce before any
  // arithmetic, else `shares + 1e-9` becomes a string concat and the oversell
  // guard misfires (selling exactly the held amount was wrongly blocked).
  const heldShares = holding ? Number(holding.shares) || 0 : 0;
  const heldAvg = holding ? Number(holding.avg_cost) || 0 : 0;
  const heldCur = holding && holding.current_price != null ? Number(holding.current_price) : NaN;

  const price = parseFloat(priceStr);
  const qty = parseFloat(qtyStr);
  const commission = parseFloat(commissionStr) || 0;
  const priceValid = Number.isFinite(price) && price > 0;
  const qtyValid = Number.isFinite(qty) && qty > 0;
  const cash = Number(detail?.cash_balance ?? 0) || 0;
  const livePrice = !Number.isNaN(heldCur) && heldCur > 0 ? heldCur : (isInitialTicker ? (prefillPrice ?? price) : price);

  const estCost = priceValid && qtyValid ? qty * price + (side === 'buy' ? commission : -commission) : 0;
  const overspend = side === 'buy' && priceValid && qtyValid && (qty * price + commission) > cash + 1e-6;
  const oversell = side === 'sell' && qtyValid && holding != null && qty > heldShares + 1e-9;
  const sellNoHolding = side === 'sell' && (holding == null || heldShares <= 0);

  const canSubmit = !!selectedId && tickerUC.length > 0 && priceValid && qtyValid && !submitting && !oversell && !sellNoHolding;

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await addTransaction(selectedId, {
        ticker: tickerUC,
        action: side,
        shares: qty,
        price,
        commission: commission || undefined,
        transactedAt: dateTimeStr ? new Date(dateTimeStr).toISOString() : undefined,
      });
      try { window.localStorage.setItem(LAST_PORTFOLIO_KEY, selectedId); } catch { /* ignore */ }
      setSuccess(`${side === 'buy' ? 'Bought' : 'Sold'} ${qty} ${tickerUC} @ ${formatCurrency(price)}`);
      onTraded?.();
      try { setDetail(await getPortfolio(selectedId)); } catch { /* ignore */ }
      setQtyStr('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Trade failed');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, selectedId, tickerUC, side, qty, price, commission, dateTimeStr, onTraded]);

  const unrealizedPct = heldAvg > 0 ? livePrice / heldAvg - 1 : 0;
  const unrealizedUsd = holding ? (livePrice - heldAvg) * heldShares : 0;

  return (
    <div className={cn('space-y-2.5', className)}>
      {/* Ticker (editable) */}
      <div>
        <label className="block text-[11px] text-fg-subtle mb-1">Ticker</label>
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="AAPL"
          spellCheck={false}
          className="w-full bg-surface border border-default rounded-md px-2.5 py-2 text-sm text-white font-mono uppercase focus:outline-none focus:border-sky-500"
        />
      </div>

      {/* Portfolio picker */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[11px] text-fg-subtle">Portfolio</label>
          {selectedId && (
            <a
              href={`/portfolio/${selectedId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1"
              title="Open this portfolio in a new tab"
            >
              Open <ExternalLink size={11} />
            </a>
          )}
        </div>
        {loadErr ? (
          <div className="flex items-center justify-between gap-2 text-xs text-rose-300">
            <span className="truncate">Couldn&apos;t load portfolios.</span>
            <button onClick={() => setReloadKey((k) => k + 1)} className="flex-shrink-0 text-sky-400 hover:text-sky-300">Retry</button>
          </div>
        ) : portfolios == null ? (
          <div className="flex items-center gap-1.5 text-xs text-fg-subtle"><Loader2 size={12} className="animate-spin" /> Loading…</div>
        ) : portfolios.length === 0 ? (
          <div className="text-xs text-fg-subtle">You have no portfolios yet.</div>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value);
              try { window.localStorage.setItem(LAST_PORTFOLIO_KEY, e.target.value); } catch { /* ignore */ }
            }}
            className="w-full bg-surface border border-default rounded-md px-2.5 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
          >
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Holding panel */}
      {selectedId && (
        <div className="rounded-md border border-default bg-surface/50 px-3 py-2 text-xs">
          {detailLoading ? (
            <span className="text-fg-subtle inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Loading holding…</span>
          ) : holding ? (
            <div className="flex items-center justify-between gap-2">
              <div className="text-fg-muted">
                Holding <span className="font-mono text-white">{heldShares}</span> @ {formatCurrency(heldAvg)}
                <span className={cn('ml-2', unrealizedUsd >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]')}>
                  {unrealizedUsd >= 0 ? '+' : ''}{formatCurrency(unrealizedUsd)} ({formatPct(unrealizedPct)})
                </span>
              </div>
              <button
                onClick={() => { setSide('sell'); setQtyStr(String(heldShares)); }}
                className="text-[11px] px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 flex-shrink-0"
              >
                Sell all
              </button>
            </div>
          ) : (
            <span className="text-fg-subtle">No position in this portfolio · cash {formatCurrency(cash)}</span>
          )}
        </div>
      )}

      {/* Buy / Sell toggle */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setSide('buy')}
          className={cn('inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-semibold border transition-colors',
            side === 'buy' ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300' : 'border-default text-fg-muted hover:text-white')}
        >
          <ArrowUpCircle size={15} /> Buy
        </button>
        <button
          onClick={() => setSide('sell')}
          className={cn('inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-semibold border transition-colors',
            side === 'sell' ? 'bg-rose-500/15 border-rose-500/50 text-rose-300' : 'border-default text-fg-muted hover:text-white')}
        >
          <ArrowDownCircle size={15} /> Sell
        </button>
      </div>

      {/* Price + Quantity — price field wider, quantity narrower. */}
      <div className="grid grid-cols-[1.3fr_0.7fr] gap-2">
        <div>
          <label className="block text-[11px] text-fg-subtle mb-1">Price</label>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle text-sm">$</span>
            <input
              type="number" inputMode="decimal" step="0.01" min="0" value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              className="w-full bg-surface border border-default rounded-md pl-6 pr-2.5 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] text-fg-subtle mb-1">Quantity</label>
          <input
            type="number" inputMode="decimal" step="1" min="0" value={qtyStr} placeholder="0"
            onChange={(e) => setQtyStr(e.target.value)}
            className="w-full bg-surface border border-default rounded-md px-2.5 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500"
          />
        </div>
      </div>

      {/* Commission (optional) + estimate */}
      <div className="flex items-end justify-between gap-2">
        <div className="w-28">
          <label className="block text-[11px] text-fg-subtle mb-1">Commission</label>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle text-sm">$</span>
            <input
              type="number" inputMode="decimal" step="0.01" min="0" value={commissionStr} placeholder="0"
              onChange={(e) => setCommissionStr(e.target.value)}
              className="w-full bg-surface border border-default rounded-md pl-6 pr-2 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500"
            />
          </div>
        </div>
        <div className="text-right text-xs flex-1">
          <div className="text-fg-subtle">Est. {side === 'buy' ? 'cost' : 'proceeds'}</div>
          <div className="font-mono text-sm text-white">{formatCurrency(Math.abs(estCost))}</div>
        </div>
      </div>

      {/* Transaction date/time — prefilled from the replay cursor (the price's
          time) in replay mode, else now; editable. */}
      <div>
        <label className="block text-[11px] text-fg-subtle mb-1">
          Date / time
          {effAsOfLabel && <span className="text-amber-300"> · {effAsOfLabel}</span>}
        </label>
        <input
          type="datetime-local"
          value={dateTimeStr}
          onChange={(e) => setDateTimeStr(e.target.value)}
          className="w-full bg-surface border border-default rounded-md px-2.5 py-2 text-sm text-white font-mono focus:outline-none focus:border-sky-500"
        />
      </div>

      {/* Inline warnings */}
      {oversell && <div className="text-[11px] text-rose-300">Can&apos;t sell more than the {heldShares} shares held.</div>}
      {sellNoHolding && <div className="text-[11px] text-rose-300">No shares to sell in this portfolio.</div>}
      {overspend && <div className="text-[11px] text-amber-300">Cost exceeds cash balance ({formatCurrency(cash)}); the trade will be rejected.</div>}
      {error && <div className="text-[11px] text-rose-300">{error}</div>}
      {success && <div className="text-[11px] text-emerald-300">{success}</div>}

      {/* Submit */}
      <button
        onClick={() => void onSubmit()}
        disabled={!canSubmit}
        className={cn('w-full py-2 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
          side === 'buy' ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/30'
            : 'bg-rose-500/20 border border-rose-500/50 text-rose-200 hover:bg-rose-500/30')}
      >
        {submitting && <Loader2 size={14} className="animate-spin" />}
        {side === 'buy' ? 'Buy' : 'Sell'} {qtyValid ? qty : ''} {tickerUC}
      </button>
    </div>
  );
}

export default TradeForm;
